// DragDropFlowChart.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { truncateText } from '../../utils';

const getPosition = (xOffset = 100, yOffset = 250) => ({ x: xOffset, y: yOffset });

const transformHierarchyToNodesAndEdges = (data, nodes = [], edges = [], level = 0, parentId = null, index = 0) => {
  data.forEach((item, idx) => {
    const nodeId = item.id.toString();
    nodes.push({
      id: nodeId,
      data: { label: item.role_name },
      position: getPosition(200 * (index + idx), 90 * level),
      type: 'default',
    });

    if (parentId) {
      edges.push({
        id: `e${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: 'smoothstep',
      });
    }

    if (item.children?.length) {
      transformHierarchyToNodesAndEdges(item.children, nodes, edges, level + 1, nodeId, index + idx);
    }
  });

  return { nodes, edges };
};

const DragDropFlowChart = ({ data, loadData, dragItems:roles, onAddNode, onAddEdge, onDeleteNode }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeCount, setNodeCount] = useState(100);

  useEffect(() => {
    const { nodes: initialNodes, edges: initialEdges } = transformHierarchyToNodesAndEdges(data);
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [data]);

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();
      const roleData = JSON.parse(event.dataTransfer.getData('application/reactflow'));
      const position = { x: event.clientX - 250, y: event.clientY - 100 };
      const newId = roleData.id.toString();
      const newNode = {
        id: newId,
        data: { label: roleData.name },
        position,
        type: 'default',
      };

      try {
        await onAddNode?.({ id: newId, name: roleData.name, position });
        await loadData();
        setNodes((nds) => nds.concat(newNode));
        setNodeCount((count) => count + 1);
      } finally {
      }

      setNodeCount((count) => count + 1);
      setNodes((nds) => nds.concat(newNode));
    },
    [nodeCount, setNodes]
  );

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onConnect = useCallback(
    async (params) => {
      const newEdge = addEdge(params, edges);
      try {
        await onAddEdge?.({ source: params.source, target: params.target });
        await loadData();
        setEdges((eds) => addEdge(newEdge, eds));
      } finally {
      }
    },
    [edges, setEdges, loadData]
  );

  const onNodesDelete = useCallback(
    async (deleted) => {
     
      try {
        for (const node of deleted) {
          await onDeleteNode?.(node);
          await loadData();
        }
      } finally {
        
      }
    },
    [loadData]
  );

  const handleDragStart = (event, role) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(role));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: 200, padding: 10, borderRight: '1px solid #ccc' }}>
        <h4>Roles</h4>
        {roles.length === 0 && <div>No roles available</div> }
        {roles.map((role) => (
          <div
            key={role.id}
            onDragStart={(event) => handleDragStart(event, role)}
            draggable
            style={{ padding: '8px', marginBottom: '10px', border: '1px solid gray', borderRadius: 4, cursor: 'grab' }}
          >
            {truncateText(role.name, 15)}
          </div>
        ))}
        <div className="mt-2 text-muted small">
          Note: Drag the Roles to the canvas in the right side, inorder to create hierarchy.
          To delete a role, select the role from the hierarchy and press Backspace.
        </div>
      </div>
      <div style={{ flexGrow: 1, height: '90vh' }} onDrop={onDrop} onDragOver={onDragOver}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodesDelete={onNodesDelete}
          defaultViewport={{ x: 150, y: 100, zoom: 1 }}
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </div>
  );
};

export default DragDropFlowChart;
